import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from '../components/LegalPageLayout';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    document.title = 'Privacy Policy — Buy Drop';
  }, []);

  return (
    <LegalPageLayout title="Privacy Policy">
      <p className="text-sm leading-relaxed mb-6">
        This Privacy Policy describes how Buy Drop (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects,
        uses, and shares information when you use our websites, mobile applications, and related services
        (collectively, the &quot;Services&quot;).
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">1. Information we collect</h2>
      <ul className="list-disc pl-5 text-sm leading-relaxed space-y-2 mb-4">
        <li>
          <strong className="text-gray-900">Account data:</strong> name, email, username, password (hashed), and
          profile details you choose to provide.
        </li>
        <li>
          <strong className="text-gray-900">Transaction data:</strong> orders, listings, messages, delivery or pickup
          details, and payment-related metadata processed by our payment partners.
        </li>
        <li>
          <strong className="text-gray-900">Device and usage data:</strong> IP address, device type, browser, general
          location (e.g. region), and how you interact with the Services.
        </li>
        <li>
          <strong className="text-gray-900">Cookies and similar technologies:</strong> as described in our{' '}
          <Link to="/cookies" className="text-green-700 font-medium hover:underline">
            Cookie Policy
          </Link>
          .
        </li>
      </ul>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">2. How we use information</h2>
      <p className="text-sm leading-relaxed mb-4">We use information to:</p>
      <ul className="list-disc pl-5 text-sm leading-relaxed space-y-2 mb-4">
        <li>Provide, secure, and improve the Services;</li>
        <li>Process transactions and communicate about orders;</li>
        <li>Detect fraud, abuse, and technical issues;</li>
        <li>Comply with law and enforce our terms; and</li>
        <li>Send service-related messages and, where allowed, marketing (you may opt out where applicable).</li>
      </ul>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">3. Sharing</h2>
      <p className="text-sm leading-relaxed mb-4">
        We may share information with service providers who assist us (hosting, analytics, payments, email), with other
        users as needed to complete a transaction (e.g. seller sees buyer contact for delivery), when required by law,
        or in connection with a merger or asset sale. We do not sell your personal information for money as that term
        is commonly understood in applicable privacy laws, except as stated if we change this policy.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">4. Retention</h2>
      <p className="text-sm leading-relaxed mb-4">
        We retain information as long as needed to provide the Services, comply with legal obligations, resolve
        disputes, and enforce our agreements.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">5. Security</h2>
      <p className="text-sm leading-relaxed mb-4">
        We use reasonable technical and organizational measures to protect information. No method of transmission over
        the internet is completely secure.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">6. Your choices</h2>
      <p className="text-sm leading-relaxed mb-4">
        Depending on your location, you may have rights to access, correct, delete, or export personal data, or to
        object to certain processing. Contact us to exercise these rights. You can update account settings in the
        product where available.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">7. Children</h2>
      <p className="text-sm leading-relaxed mb-4">
        The Services are not directed at children under 13 (or the minimum age in your jurisdiction). We do not
        knowingly collect personal information from children.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">8. International users</h2>
      <p className="text-sm leading-relaxed mb-4">
        If you use the Services from outside the United States, information may be processed in the United States or
        other countries where we or our providers operate.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">9. Changes</h2>
      <p className="text-sm leading-relaxed mb-4">
        We may update this Privacy Policy from time to time. We will post the revised policy on this page and update
        the &quot;Last updated&quot; date.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">10. Contact</h2>
      <p className="text-sm leading-relaxed">
        Privacy questions:{' '}
        <a href="mailto:support@buydrop.com" className="text-green-700 font-medium hover:underline">
          support@buydrop.com
        </a>
        .
      </p>
    </LegalPageLayout>
  );
};

export default PrivacyPolicy;
